import { ChevronLeft, ChevronRight } from "lucide-react";
import type { ReactNode } from "react";
import { Button } from "../atoms/Button";
import { cn } from "../lib/cn";

export interface PaginationProps {
	page: number;
	pageCount: number;
	onPageChange: (page: number) => void;
	className?: string;
}

type PageEntry =
	| { kind: "page"; value: number }
	| { kind: "ellipsis"; id: string };

function buildPageEntries(page: number, pageCount: number): PageEntry[] {
	if (pageCount <= 7) {
		return Array.from({ length: pageCount }, (_, i) => ({
			kind: "page" as const,
			value: i + 1,
		}));
	}

	const entries: PageEntry[] = [{ kind: "page", value: 1 }];

	if (page > 3) {
		entries.push({ kind: "ellipsis", id: "start" });
	}

	const start = Math.max(2, page - 1);
	const end = Math.min(pageCount - 1, page + 1);

	for (let i = start; i <= end; i++) {
		entries.push({ kind: "page", value: i });
	}

	if (page < pageCount - 2) {
		entries.push({ kind: "ellipsis", id: "end" });
	}

	entries.push({ kind: "page", value: pageCount });

	return entries;
}

function Pagination({
	page,
	pageCount,
	onPageChange,
	className,
}: PaginationProps): ReactNode {
	if (pageCount <= 1) {
		return null;
	}

	const entries = buildPageEntries(page, pageCount);

	return (
		<nav
			aria-label="Pagination"
			className={cn("flex items-center gap-1", className)}
		>
			<Button
				variant="outline"
				size="sm"
				onClick={() => onPageChange(page - 1)}
				disabled={page <= 1}
				aria-label="Previous page"
				className="h-8 w-8 p-0"
			>
				<ChevronLeft className="h-4 w-4" aria-hidden="true" />
			</Button>

			{entries.map((entry) => {
				if (entry.kind === "ellipsis") {
					return (
						<span
							key={`ellipsis-${entry.id}`}
							className="px-1 text-sm text-mutedForeground"
							aria-hidden="true"
						>
							&hellip;
						</span>
					);
				}
				return (
					<Button
						key={entry.value}
						variant={entry.value === page ? "default" : "outline"}
						size="sm"
						onClick={() => onPageChange(entry.value)}
						aria-label={`Page ${entry.value}`}
						aria-current={entry.value === page ? "page" : undefined}
						className="h-8 w-8 p-0"
					>
						{entry.value}
					</Button>
				);
			})}

			<Button
				variant="outline"
				size="sm"
				onClick={() => onPageChange(page + 1)}
				disabled={page >= pageCount}
				aria-label="Next page"
				className="h-8 w-8 p-0"
			>
				<ChevronRight className="h-4 w-4" aria-hidden="true" />
			</Button>
		</nav>
	);
}
Pagination.displayName = "Pagination";

export default Pagination;
export { Pagination };
